import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { AccessControl, MuiButton } from "@ryde/controls";
import {
  DrawerType,
  IRichViewLogsProps,
  productionUiStateSelector,
  useDrawers,
} from "@ryde/shared";

import { useProductionViewLogs } from "./hooks";

interface IProductionViewLogsButton extends Omit<IRichViewLogsProps, "jobId"> {
  productionId?: number;
}

export const ProductionViewLogsButton: FC<IProductionViewLogsButton> = ({
  logsFormWatched,
  logsFromWatchedApiOld,
  productionId,
  logsCount,
  productionFilters,
}) => {
  const { t } = useTranslation();
  const { openDrawer } = useDrawers();
  const { isLoading } = useProductionViewLogs();
  const produceState = useRecoilValue(productionUiStateSelector);
  const [isOpenViewLogDrawerAllowed, setIsOpenViewLogDrawerAllowed] =
    useState(false);

  useEffect(() => {
    if (isLoading) {
      setIsOpenViewLogDrawerAllowed(true);
    }
  }, [isLoading]);

  const isButtonShown = useMemo(() => {
    const isPreviousProduce =
      productionId &&
      produceState?.productionId &&
      produceState?.productionId !== productionId;
    const effectiveLogsCount = logsCount ?? produceState?.logsCount;
    const hasHistoricLogsCount = Number(logsCount) > 0;
    const hasLiveLogsCount =
      Boolean(effectiveLogsCount) &&
      !isPreviousProduce &&
      isOpenViewLogDrawerAllowed;

    return (
      !!logsFormWatched?.records?.length ||
      !!logsFromWatchedApiOld?.length ||
      hasHistoricLogsCount ||
      hasLiveLogsCount
    );
  }, [
    isOpenViewLogDrawerAllowed,
    logsCount,
    logsFormWatched?.records?.length,
    logsFromWatchedApiOld?.length,
    produceState?.logsCount,
    produceState?.productionId,
    productionId,
  ]);

  const handleClick = useEvent((): void => {
    openDrawer({
      drawerProps: {
        type: DrawerType.ProductionViewLogs,
        productionId,
        logsFormWatched,
        logsFromWatchedApiOld,
        productionFilters,
      },
    });
  });

  return (
    <>
      {isButtonShown && (
        <AccessControl entity={"rideProduction"} action={"view_logs"}>
          <MuiButton
            e2eData="productions_view_logs"
            muiStyle="warning"
            onClick={handleClick}
          >
            {t(`rideProduction.viewLogs`)}
          </MuiButton>
        </AccessControl>
      )}
    </>
  );
};
